import { useMemo } from "react"

import { useResearchPreferencesStore } from "@/stores/research-preferences-store"

import { useGetTrustedSources } from "./use-get-trusted-sources"

/**
 * Hook to manage trusted sources selection state and actions
 * Consolidates logic for selecting, toggling, and filtering trusted sources
 */
export function useTrustedSourcesSelection(sessionType: string) {
  const { data: sourcesData } = useGetTrustedSources()
  const sources = sourcesData?.sources || []

  const researchPrefsStore = useResearchPreferencesStore(sessionType)
  const researchPrefs = researchPrefsStore((state) => state.prefs)
  const setSourceUrls = researchPrefsStore((state) => state.setSourceUrls)
  const setTrustedMode = researchPrefsStore((state) => state.setTrustedMode)

  // Parse selected URLs from store
  const selectedUrls = useMemo(
    () => (researchPrefs.sourceUrls ? researchPrefs.sourceUrls.split("\n").filter(Boolean) : []),
    [researchPrefs.sourceUrls]
  )

  // Filter sources by type
  const personalSources = useMemo(() => sources.filter((s) => s.type === "personal"), [sources])

  const companySources = useMemo(() => sources.filter((s) => s.type === "company"), [sources])

  // Calculate all unique URLs and selection state
  const allUrls = useMemo(() => Array.from(new Set(sources.map((s) => s.url))), [sources])

  const isAllSelected = selectedUrls.length > 0 && selectedUrls.length === allUrls.length

  // Toggle a single source
  const toggleSource = (url: string, checked: boolean) => {
    const urls = new Set(selectedUrls)
    if (checked) {
      urls.add(url)
    } else {
      urls.delete(url)
    }
    setSourceUrls(Array.from(urls).join("\n"))
  }

  // Toggle all sources on/off
  const toggleAll = () => {
    if (isAllSelected) {
      setSourceUrls("")
    } else {
      setSourceUrls(allUrls.join("\n"))
    }
  }

  return {
    sources,
    personalSources,
    companySources,
    selectedUrls,
    allUrls,
    isAllSelected,
    toggleSource,
    toggleAll,
    trustedMode: researchPrefs.trustedMode,
    setTrustedMode,
  }
}
